/**
 * Менеджер плагинов для CSElement
 * Обеспечивает загрузку, управление и выполнение плагинов
 */

import { EventEmitter } from 'eventemitter3';
import {
  CSElementPlugin,
  PluginManager as IPluginManager,
  LifecycleHooks,
  MiddlewareContext,
  MiddlewareConfig,
  PluginInfo,
  PluginEvent,
  PluginOptions
} from '../types/plugin-interfaces';
import { AdvancedMiddlewareManagerImpl } from './AdvancedMiddlewareManager';

export class PluginManager extends EventEmitter implements IPluginManager {
  private plugins: Map<string, CSElementPlugin> = new Map();
  private pluginOptions: Map<string, PluginOptions> = new Map();
  private hooks: Map<keyof LifecycleHooks, Function[]> = new Map();
  private advancedMiddleware: AdvancedMiddlewareManagerImpl = new AdvancedMiddlewareManagerImpl();
  private CSElementClass: any = null;

  constructor() {
    super();
    this.initializeHooks();
  }

  /**
   * Инициализация хуков
   */
  private initializeHooks(): void {
    const hookNames: (keyof LifecycleHooks)[] = [
      'beforeCreate', 'afterCreate',
      'beforeAddElement', 'afterAddElement',
      'beforeRemoveElement', 'afterRemoveElement',
      'beforeSetData', 'afterSetData',
      'beforeDeleteData', 'afterDeleteData',
      'beforeDestroy', 'afterDestroy'
    ];

    hookNames.forEach(hookName => {
      this.hooks.set(hookName, []);
    });
  }

  /**
   * Установить ссылку на класс CSElement
   */
  setCSElementClass(CSElementClass: any): void {
    this.CSElementClass = CSElementClass;
  }

  /**
   * Установить плагин
   */
  use(plugin: CSElementPlugin, options?: PluginOptions): void {
    try {
      // Проверка зависимостей
      if (plugin.dependencies) {
        for (const dependency of plugin.dependencies) {
          if (!this.hasPlugin(dependency)) {
            throw new Error(
              `Плагин "${plugin.name}" требует зависимость "${dependency}", которая не установлена`
            );
          }
        }
      }

      // Проверка на дублирование
      if (this.hasPlugin(plugin.name)) {
        throw new Error(`Плагин "${plugin.name}" уже установлен`);
      }

      // Установка плагина
      if (!this.CSElementClass) {
        throw new Error('CSElement класс не установлен в менеджере плагинов');
      }

      plugin.install(this.CSElementClass, options);
      
      // Сохранение плагина
      this.plugins.set(plugin.name, plugin);
      this.pluginOptions.set(plugin.name, options || {});

      // Событие установки
      const event: PluginEvent = {
        type: 'install',
        plugin: plugin.name,
        data: { version: plugin.version, options },
        timestamp: Date.now()
      };
      
      this.emit('plugin:installed', event);
      
      console.log(`✅ Плагин "${plugin.name}" v${plugin.version} успешно установлен`);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const event: PluginEvent = {
        type: 'error',
        plugin: plugin.name,
        data: { error: errorMessage },
        timestamp: Date.now()
      };
      
      this.emit('plugin:error', event);
      throw error;
    }
  }

  /**
   * Удалить плагин
   */
  uninstall(pluginName: string): boolean {
    try {
      const plugin = this.plugins.get(pluginName);
      if (!plugin) {
        return false;
      }

      // Проверка зависимостей других плагинов
      for (const [name, installedPlugin] of this.plugins) {
        if (name !== pluginName && installedPlugin.dependencies?.includes(pluginName)) {
          throw new Error(
            `Нельзя удалить плагин "${pluginName}": от него зависит плагин "${name}"`
          );
        }
      }

      // Удаление плагина
      if (plugin.uninstall) {
        plugin.uninstall();
      }

      this.plugins.delete(pluginName);
      this.pluginOptions.delete(pluginName);

      // Событие удаления
      const event: PluginEvent = {
        type: 'uninstall',
        plugin: pluginName,
        timestamp: Date.now()
      };
      
      this.emit('plugin:uninstalled', event);
      
      console.log(`✅ Плагин "${pluginName}" успешно удален`);
      return true;
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const event: PluginEvent = {
        type: 'error',
        plugin: pluginName,
        data: { error: errorMessage },
        timestamp: Date.now()
      };
      
      this.emit('plugin:error', event);
      throw error;
    }
  }

  /**
   * Получить установленный плагин
   */
  getPlugin(name: string): CSElementPlugin | null {
    return this.plugins.get(name) || null;
  }

  /**
   * Получить все установленные плагины
   */
  getPlugins(): CSElementPlugin[] {
    return Array.from(this.plugins.values());
  }

  /**
   * Проверить, установлен ли плагин
   */
  hasPlugin(name: string): boolean {
    return this.plugins.has(name);
  }

  /**
   * Получить информацию о всех плагинах
   */
  getPluginInfo(): PluginInfo[] {
    return Array.from(this.plugins.entries()).map(([name, plugin]) => ({
      name: plugin.name,
      version: plugin.version,
      description: plugin.description,
      dependencies: plugin.dependencies || [],
      installed: true,
      options: this.pluginOptions.get(name)
    }));
  }

  /**
   * Добавить хук жизненного цикла
   */
  addHook(hookName: keyof LifecycleHooks, callback: Function): void {
    const hooks = this.hooks.get(hookName) || [];
    hooks.push(callback);
    this.hooks.set(hookName, hooks);
  }

  /**
   * Удалить хук жизненного цикла
   */
  removeHook(hookName: keyof LifecycleHooks, callback: Function): void {
    const hooks = this.hooks.get(hookName) || [];
    const index = hooks.indexOf(callback);
    if (index > -1) {
      hooks.splice(index, 1);
      this.hooks.set(hookName, hooks);
    }
  }

  /**
   * Добавить middleware с конфигурацией
   */
  addAdvancedMiddleware(operation: string, config: MiddlewareConfig): void {
    this.advancedMiddleware.addMiddleware(operation, config);
  }

  /**
   * Выполнить хуки жизненного цикла
   */
  async executeHooks(hookName: keyof LifecycleHooks, ...args: any[]): Promise<void> {
    const hooks = this.hooks.get(hookName) || [];
    
    for (const hook of hooks) {
      try {
        const result = hook(...args);
        if (result instanceof Promise) {
          await result;
        }
      } catch (error) {
        console.error(`Ошибка в хуке ${hookName}:`, error);
        this.emit('hook:error', { hookName, error, args });
      }
    }
  }

  /**
   * Выполнить middleware цепочку
   */
  async executeMiddleware(context: MiddlewareContext, operation: () => Promise<any>): Promise<any> {
    return this.advancedMiddleware.executeMiddleware(context, operation);
  }

  /**
   * Очистить все плагины (для тестирования)
   */
  clear(): void {
    // Удаляем все плагины
    for (const [name] of this.plugins) {
      try {
        this.uninstall(name);
      } catch (error) {
        console.error(`Ошибка при удалении плагина ${name}:`, error);
      }
    }

    // Очищаем хуки и middleware
    this.initializeHooks();
    
    // Очищаем advanced middleware
    this.advancedMiddleware.clearAllMiddleware();
  }

  /**
   * Получить статистику плагинов
   */
  getStats(): {
    totalPlugins: number;
    totalHooks: number;
    totalMiddleware: number;
    pluginsByType: Record<string, number>;
  } {
    const totalHooks = Array.from(this.hooks.values())
      .reduce((sum, hooks) => sum + hooks.length, 0);
    
    const advancedMiddlewareStats = this.advancedMiddleware.getGlobalStats();

    return {
      totalPlugins: this.plugins.size,
      totalHooks,
      totalMiddleware: advancedMiddlewareStats.totalMiddleware,
      pluginsByType: {} // Можно расширить для категоризации плагинов
    };
  }
} 